import { useLazyQuery } from '@apollo/client/react';
import { useCallback, useEffect, useRef } from 'react';

/**
 * A wrapper hook around Apollo Client 4's useLazyQuery that provides backward compatibility
 * with Apollo Client 3 features, specifically:
 * - onCompleted and onError callbacks
 * - variables and context options in hook options (moved to execute function in v4)
 *
 * Note: errorPolicy is handled by Apollo Client 4's useLazyQuery itself - the promise
 * resolves or rejects based on the configured errorPolicy.
 *
 * @template TData - The type of data returned by the query
 * @template TVariables - The type of variables for the query
 *
 * @param {import('@apollo/client').DocumentNode | import('@apollo/client').TypedDocumentNode<TData, TVariables>} query - The GraphQL query document parsed into an AST by gql
 * @param {Object} options - Default options to control how the query is executed
 * @param {Function} [options.onCompleted] - Callback function called when query completes successfully. Receives the data as parameter.
 * @param {Function} [options.onError] - Callback function called when query encounters an error. Receives the error as parameter.
 * @param {TVariables} [options.variables] - Variables to pass to the query (will be merged with execute function variables). In Apollo Client 4, these should be passed to execute function instead.
 * @param {import('@apollo/client').DefaultContext} [options.context] - Context to pass to the query (will be merged with execute function context). In Apollo Client 4, this should be passed to execute function instead.
 * @param {import('@apollo/client').WatchQueryFetchPolicy} [options.fetchPolicy] - Specifies how the query interacts with the Apollo Client cache during execution
 * @param {import('@apollo/client').WatchQueryFetchPolicy | Function} [options.nextFetchPolicy] - Specifies the FetchPolicy to be used after this query has completed
 * @param {import('@apollo/client').RefetchWritePolicy} [options.refetchWritePolicy] - Specifies whether a NetworkStatus.refetch operation should merge incoming field data with existing data
 * @param {('none'|'all'|'ignore')} [options.errorPolicy='none'] - Specifies how the query handles a response that returns both GraphQL errors and partial results
 * @param {number} [options.pollInterval] - Specifies the interval (in milliseconds) at which the query polls for updated results
 * @param {boolean} [options.notifyOnNetworkStatusChange=true] - If true, the in-progress query's associated component re-renders whenever the network status changes
 * @param {boolean} [options.returnPartialData=false] - If true, the query can return partial results from the cache
 * @param {Function} [options.skipPollAttempt] - A callback function that's called whenever a refetch attempt occurs while polling
 * @param {import('@apollo/client').ApolloClient} [options.client] - The instance of ApolloClient to use to execute the query
 * @returns {[Function, Object]} A tuple in the form of [execute, result] where:
 *   - execute: Function that accepts ExecOptions {variables?: TVariables, context?: DefaultContext} and returns a Promise
 *   - result: Object containing {data, loading, error, called, variables, networkStatus, refetch, fetchMore, etc.}
 */
export default function useLazyQueryWithCallbacks(query, options = {}) {
  const {
    onCompleted,
    onError,
    variables: hookVariables,
    context: hookContext,
    ...restOptions
  } = options;

  // Store callbacks in refs to avoid stale closures
  const onCompletedRef = useRef(onCompleted);
  const onErrorRef = useRef(onError);

  // Update refs when callbacks change
  useEffect(() => {
    onCompletedRef.current = onCompleted;
    onErrorRef.current = onError;
  }, [onCompleted, onError]);

  // Use Apollo Client 4's useLazyQuery without variables/context in options
  const [executeBase, result] = useLazyQuery(query, {
    ...restOptions,
  });

  // Helper function to merge variables
  const mergeVariables = useCallback(
    (executeVariables) => {
      if (!hookVariables && !executeVariables) {
        return undefined;
      }
      const merged = {};
      if (hookVariables) {
        Object.assign(merged, hookVariables);
      }
      if (executeVariables) {
        Object.assign(merged, executeVariables);
      }
      return Object.keys(merged).length > 0 ? merged : undefined;
    },
    [hookVariables],
  );

  // Helper function to merge context
  const mergeContext = useCallback(
    (executeContext) => {
      if (!hookContext && !executeContext) {
        return undefined;
      }
      const merged = {};
      if (hookContext) {
        Object.assign(merged, hookContext);
      }
      if (executeContext) {
        Object.assign(merged, executeContext);
      }
      return Object.keys(merged).length > 0 ? merged : undefined;
    },
    [hookContext],
  );

  // Helper function to build execute parameters
  const buildExecuteParams = useCallback((mergedVariables, mergedContext) => {
    const params = {};
    if (mergedVariables) {
      params.variables = mergedVariables;
    }
    if (mergedContext) {
      params.context = mergedContext;
    }
    return params;
  }, []);

  // Helper function to handle query result
  // Apollo Client 4 handles errorPolicy - promise resolves/rejects accordingly
  // We just call the appropriate callbacks based on the result
  const handleQueryResult = useCallback((queryResult) => {
    const { data, error } = queryResult;

    if (error && error.name !== 'AbortError') {
      onErrorRef.current?.(error);
    }

    // If there's an error in the result (errorPolicy: 'all'), call onError
    if (error && onErrorRef.current) {
      onErrorRef.current(error);
    }

    // If there's data, call onCompleted
    if (data && onCompletedRef.current) {
      onCompletedRef.current(data);
    }

    return queryResult;
  }, []);

  // Helper function to handle errors
  // This is called when promise rejects (errorPolicy: 'none' or network errors)
  const handleError = useCallback((error) => {
    // 🚫 Ignore aborted requests
    if (error?.name === 'AbortError') {
      return; // swallow it completely
    }

    if (onErrorRef.current) {
      onErrorRef.current(error);
    }
    // Re-throw to maintain Apollo Client 4 promise rejection behavior
    throw error;
  }, []);

  /**
   * Execute function that triggers the query execution.
   * Matches Apollo Client 4's ExecOptions interface: only accepts variables and context.
   *
   * @param {Object} [executeOptions={}] - Options for execution
   * @param {TVariables} [executeOptions.variables] - Variables to pass to the query (merged with hook-level variables)
   * @param {import('@apollo/client').DefaultContext} [executeOptions.context] - Context to pass to the query (merged with hook-level context)
   * @returns {Promise<import('@apollo/client').QueryResult<TData>>} Promise that resolves with query result
   */
  const execute = useCallback(
    async (executeOptions = {}) => {
      const { variables: executeVariables, context: executeContext } =
        executeOptions;

      const mergedVariables = mergeVariables(executeVariables);
      const mergedContext = mergeContext(executeContext);
      const executeParams = buildExecuteParams(mergedVariables, mergedContext);

      try {
        const queryResult = await executeBase(executeParams);
        return handleQueryResult(queryResult);
      } catch (error) {
        return handleError(error);
      }
    },
    [
      executeBase,
      mergeVariables,
      mergeContext,
      buildExecuteParams,
      handleQueryResult,
      handleError,
    ],
  );

  return [execute, result];
}
